import { MutationResult, useMutation } from '@reverbdotcom/commons/src/useMutation';
import {
  CheckoutCardPaymentHookCheckoutFields,
  CheckoutCardPaymentHookMeFields,
  CoreCheckoutFinalizeCardPayment,
  core_apimessages_Error as Error,
  core_apimessages_CheckoutPaymentMethod_Type as CheckoutPaymentMethodType,
  Input_core_apimessages_FinalizeCardCheckoutPaymentRequest as FinalizeCardCheckoutPaymentRequest,
} from '../schemas/rql';
import { MutationFn } from 'react-apollo';
import gql from 'graphql-tag';
import React from 'react';
import { browserInfo } from '../credit_cards/browser_info';
import { CheckoutTotalAmountFragment, findAmountOwed } from '../lib/checkoutTotalAmount';
import { mapAddressToEntry } from '../lib/addressEntryMapping';

export type CoreCheckoutFinalizeCardPaymentFn = MutationFn<CoreCheckoutFinalizeCardPayment.Mutation, CoreCheckoutFinalizeCardPayment.Variables>;

type Checkout = CheckoutCardPaymentHookCheckoutFields.Fragment;
type Me = CheckoutCardPaymentHookMeFields.Fragment;

type CreditCardInput = Partial<Pick<FinalizeCardCheckoutPaymentRequest, 'creditCardId' | 'creditCard'>>;

export interface CardPaymentForm {
  type: CheckoutPaymentMethodType.DIRECT_CHECKOUT;
  showCardWallet: boolean;
  update: (input: CreditCardInput) => void;
  submit: () => Promise<MutationResult<CoreCheckoutFinalizeCardPayment.Mutation>>;
  errors: Error[];
  loading: boolean;
  result: CoreCheckoutFinalizeCardPayment.FinalizeCardCheckoutPayment;
}

export function useCheckoutCardPaymentForm({ checkout, me, finalizeCardPayment }: {
  checkout: Checkout,
  me: Me,
  finalizeCardPayment: CoreCheckoutFinalizeCardPaymentFn,
}): CardPaymentForm {
  const [mutate, { data, errors, loading }] = useMutation(finalizeCardPayment);
  const [requestInput, setRequestInput] = React.useState<CreditCardInput>({});
  const showCardWallet = !!me?.creditCards?.length;

  const update = React.useCallback((input: CreditCardInput) => {
    setRequestInput(prevInput => ({ ...prevInput, ...input }));
  }, [setRequestInput]);

  async function submit() {
    const displayedAmountOwed = findAmountOwed(checkout);

    return mutate({
      variables: {
        input: {
          id: checkout.id,
          browserInfoJson: JSON.stringify(browserInfo()),
          displayedAmountOwed,
          creditCardId: requestInput.creditCardId,
          ...mapCreditCard(requestInput),
        },
      },
    });
  }

  return {
    type: CheckoutPaymentMethodType.DIRECT_CHECKOUT,
    showCardWallet,
    update,
    submit,
    errors,
    loading,
    result: data?.finalizeCardCheckoutPayment,
  };
}

function mapCreditCard(requestInput: CreditCardInput) {
  const creditCard = requestInput?.creditCard;

  if (!creditCard) {
    return {};
  }

  return {
    creditCard: {
      ...creditCard,
      address: mapAddressToEntry(creditCard.address),
    },
  };
}

const checkoutFragment = gql`
  fragment CheckoutCardPaymentHookCheckoutFields on Checkout {
    _id
    id
    ...CheckoutTotalAmountFields
  }
  ${CheckoutTotalAmountFragment}
`;

const meFragment = gql`
  fragment CheckoutCardPaymentHookMeFields on rql_Me {
    uuid
    creditCards {
      id
    }
  }
`;

export const fragments = {
  checkout: checkoutFragment,
  me: meFragment,
};

export const FINALIZE_CARD_PAYMENT_MUTATION = gql`
  mutation Core_Checkout_FinalizeCardPayment(
    $input: Input_core_apimessages_FinalizeCardCheckoutPaymentRequest,
  ) {
    finalizeCardCheckoutPayment(input: $input) {
      checkout {
        _id
      }
    }
  }
`;
